import React from 'react';
import { useIntl } from 'react-intl';
import { Badge } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { teamPlanLabel } from '../team/teamAcceptMessages';
import { useWorkspaces } from './useWorkspaces';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

/**
 * design: workspaces e1 "Workspace" panel. A member sees the name, the plan and the owner of the
 * active workspace, read only. Nothing while `workspaces.list` loads. ErrorView with a retry when it fails.
 */
const MemberWorkspacePanelComponent: React.FC = () => {
  const intl = useIntl();
  const { active, error, refetch } = useWorkspaces();
  if (error !== null) {
    return (
      <Panel as="section" className="mb-4">
        <PanelHead title={intl.formatMessage({ id: 'app.sml.workspaces.rename.title', defaultMessage: 'Workspace' })} />
        <PanelBody>
          <ErrorView error={error} retry={() => refetch().catch(console.error)} />
        </PanelBody>
      </Panel>
    );
  }
  if (active === null) {
    return null;
  }
  return (
    <Panel as="section" className="mb-4">
      <PanelHead
        title={intl.formatMessage({ id: 'app.sml.workspaces.rename.title', defaultMessage: 'Workspace' })}
        right={intl.formatMessage({ id: 'app.sml.team.role.member', defaultMessage: 'Member' })}
      />
      <PanelBody>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <b>{active.name}</b>
          <Badge color="secondary" pill>
            {teamPlanLabel(intl, active.plan)}
          </Badge>
        </div>
        <p className="text-muted small mb-0" data-clarity-mask="true">
          {intl.formatMessage(
            {
              id: 'app.sml.workspaces.member.ownedBy',
              defaultMessage:
                'Owned by {email}. Only the owner can rename the workspace, invite people and change the plan.',
            },
            { email: active.ownerEmail }
          )}
        </p>
      </PanelBody>
    </Panel>
  );
};

export const MemberWorkspacePanel = TypedMemo(MemberWorkspacePanelComponent);
